import React, { useMemo, useEffect } from 'react';
import { Button } from './ui/Button';

interface Detection {
  matchedText: string;
  sessionId: string;
  speaker?: string;
}

interface CommonLandmineData {
  sourceId: string | null;
  trigger: string;
  triggerVariations: string[];
  detections: Detection[];
  totalCount: number;
  landmineType?: 'legal_risk' | 'detractor';
  category?: string;
  priority: 'high' | 'medium' | 'low';
}

interface CommonLandminesModalProps {
  isOpen: boolean;
  onClose: () => void;
  landmines: CommonLandmineData[];
  loading: boolean;
}

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'high':
      return { bg: 'bg-error-tint', border: 'border-error', badge: 'bg-error', text: 'text-error' };
    case 'medium':
      return { bg: 'bg-warning-tint', border: 'border-warning', badge: 'bg-warning', text: 'text-warning' };
    case 'low':
    default:
      return { bg: 'bg-surface-subtle', border: 'border-ink-border', badge: 'bg-ink-secondary', text: 'text-ink-secondary' };
  }
};

const CommonLandminesModal: React.FC<CommonLandminesModalProps> = ({
  isOpen,
  onClose,
  landmines,
  loading
}) => {
  // Calculate total detections
  const totalDetections = useMemo(() => {
    return landmines.reduce((sum, l) => sum + l.totalCount, 0);
  }, [landmines]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop">
      <div className="relative max-w-5xl w-full mx-4 max-h-[90vh] flex flex-col modal-panel">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h3 className="heading-3">Common Landmine Detections</h3>
            <p className="text-sm text-ink-secondary mt-1">
              {landmines.length} unique landmines | {totalDetections} total detections
            </p>
          </div>
          <Button
            variant="tertiary"
            size="sm"
            onClick={onClose}
            className="!p-1"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 pt-4">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-signature"></div>
            </div>
          ) : landmines.length === 0 ? (
            <div className="text-center py-12 text-ink-muted">
              No landmines detected for the selected period
            </div>
          ) : (
            <div className="space-y-4">
              {landmines.map((landmine, index) => {
                const colors = getPriorityColor(landmine.priority);

                return (
                  <div
                    key={landmine.sourceId || index}
                    className={`p-4 rounded-lg border ${colors.border} ${colors.bg}`}
                  >
                    {/* Header with count badge and priority */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 text-sm font-bold text-white ${colors.badge} rounded-full`}>
                          {landmine.totalCount}x
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium ${colors.text} bg-white bg-opacity-50 rounded-full`}>
                          {landmine.priority?.toUpperCase()} Priority
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          landmine.landmineType === 'detractor'
                            ? 'bg-surface-sunken text-ink-secondary'
                            : 'text-ink-secondary bg-surface-subtle'
                        }`}>
                          {landmine.landmineType === 'detractor' ? 'Detractor' : (landmine.category || 'Other')}
                        </span>
                      </div>
                      <span className="text-xs text-ink-muted">
                        #{index + 1}
                      </span>
                    </div>

                    {/* Trigger Word */}
                    <div className="mb-3">
                      <div className="text-xs font-medium text-ink-muted mb-1 flex items-center gap-1">
                        <span className="inline-block w-2 h-2 bg-ink-muted rounded-full"></span>
                        Trigger Word/Phrase:
                      </div>
                      <div className="text-lg font-bold text-ink pl-3 border-l-2 border-ink-border">
                        "{landmine.trigger}"
                      </div>
                    </div>

                    {/* Trigger Variations (from knowledge base) */}
                    {landmine.triggerVariations && landmine.triggerVariations.length > 0 && (
                      <div className="mb-3">
                        <div className="text-xs font-medium text-ink-muted mb-1 flex items-center gap-1">
                          <span className="inline-block w-2 h-2 bg-ink-muted rounded-full"></span>
                          Known Variations ({landmine.triggerVariations.length}):
                        </div>
                        <div className="flex flex-wrap gap-1 pl-3">
                          {landmine.triggerVariations.map((variation, vIndex) => (
                            <span
                              key={vIndex}
                              className="px-2 py-1 text-xs bg-surface rounded border border-hairline"
                            >
                              {variation}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Detected in Transcripts */}
                    <div>
                      <div className="text-xs font-medium text-ink-muted mb-2 flex items-center gap-1">
                        <span className="inline-block w-2 h-2 bg-ink-muted rounded-full"></span>
                        Detected in Transcripts ({landmine.detections?.length || 0} detection{(landmine.detections?.length || 0) !== 1 ? 's' : ''}):
                      </div>
                      <div className="pl-3 space-y-1 max-h-48 overflow-y-auto">
                        {landmine.detections?.map((detection, dIndex) => (
                          <div
                            key={dIndex}
                            className="text-sm text-ink-secondary py-1 px-2 bg-surface rounded border-l-2 border-hairline flex justify-between items-start gap-2"
                          >
                            <span>
                              "{detection.matchedText}"
                              {detection.speaker && (
                                <span className="text-xs text-ink-muted ml-1">
                                  ({detection.speaker === 'customer' ? 'Customer' : 'Salesperson'})
                                </span>
                              )}
                            </span>
                            <span className="text-xs text-ink-muted whitespace-nowrap">
                              [{detection.sessionId || 'N/A'}]
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CommonLandminesModal;
